import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getAttendance,
  checkIn,
  checkOut,
  getEmployees,
  clearError,
  clearSuccess,
} from "../../../../redux/Slices/authSlice";
import {
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaSignInAlt,
  FaSignOutAlt,
  FaPlus,
  FaPrint,
  FaFileExcel,
} from "react-icons/fa";
import SortOrderSelect from "../../../../shared/components/SortOrderSelect";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import {
  chunkArray,
  getPdfRowStyles,
  toPositiveInteger,
} from "../../../../shared/utils.js/utils";
import {
  getGenericResponseCache,
  isOnline,
} from "../../../../shared/utils.js/offlineQueue";
import { BASE_URL } from "../../../../shared/utils.js/utils";

const normalizeListResponse = payload => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  // Normalize pagination to ensure it has current_page and last_page
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const Attendance = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const { attendance, attendancePagination, isLoading, error, success } =
    useSelector(state => state.auth);

  // Employee dropdown state for check in
  const [checkInEmployeeDropdownOpen, setCheckInEmployeeDropdownOpen] =
    useState(false);
  const [checkInEmployeeSearchTerm, setCheckInEmployeeSearchTerm] =
    useState("");
  const [checkInEmployeeOptions, setCheckInEmployeeOptions] = useState([]);
  const [checkInEmployeePage, setCheckInEmployeePage] = useState(1);
  const [checkInEmployeePagination, setCheckInEmployeePagination] =
    useState(null);
  const checkInEmployeeSearchInputRef = useRef(null);

  // Employee dropdown state for check out
  const [checkOutEmployeeDropdownOpen, setCheckOutEmployeeDropdownOpen] =
    useState(false);
  const [checkOutEmployeeSearchTerm, setCheckOutEmployeeSearchTerm] =
    useState("");
  const [checkOutEmployeeOptions, setCheckOutEmployeeOptions] = useState([]);
  const [checkOutEmployeePage, setCheckOutEmployeePage] = useState(1);
  const [checkOutEmployeePagination, setCheckOutEmployeePagination] =
    useState(null);
  const checkOutEmployeeSearchInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || "",
  );
  const [fromDate, setFromDate] = useState(searchParams.get("from_date") || "");
  const [toDate, setToDate] = useState(searchParams.get("to_date") || "");
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [checkInData, setCheckInData] = useState({
    employee_id: "",
    check_in_notes: "",
  });
  const [checkOutData, setCheckOutData] = useState({
    employee_id: "",
    check_out_notes: "",
  });
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10); // القيمة المطبقة فعلياً
  const [perPageInput, setPerPageInput] = useState(10); // القيمة الظاهرة في الـ UI
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' أو 'desc'
  const [sortField, setSortField] = useState("id"); // 'id' | 'employee' | 'work_date' | 'status'
  const [allAttendance, setAllAttendance] = useState([]);
  const [allCachedAttendance, setAllCachedAttendance] = useState([]); // All attendance from cache for offline filtering
  const [pagination, setPagination] = useState(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const [attendanceForPrint, setAttendanceForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [measureChunk, setMeasureChunk] = useState(null);
  const [isSinglePreviewOpen, setIsSinglePreviewOpen] = useState(false);
  const [attendanceForSinglePreview, setAttendanceForSinglePreview] = useState(null);
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;
  const searchTimeoutRef = useRef(null);

  const preloadImage = src =>
    new Promise(resolve => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  // Calculates how many rows fit on a page by measuring actual rendered heights.
  // Uses a small off-screen sample template (attendance-pdf-measure) to read
  // the real chrome height (header + title + table-header + footer + padding/borders)
  // then derives: rowsPerPage = floor((targetHeight - chromeHeight) / avgRowHeight)
  const getDynamicRowsPerPage = async records => {
    if (!records || records.length === 0) return 20;
    const sample = records.slice(0, Math.min(5, records.length));
    setMeasureChunk(sample);

    await new Promise(r => requestAnimationFrame(r));
    await new Promise(r => requestAnimationFrame(r));
    await new Promise(r => setTimeout(r, 250));

    try {
      const el = document.getElementById("attendance-pdf-measure");
      if (!el) return 20;

      const totalH = el.offsetHeight;
      const rows = el.querySelectorAll("tbody tr");
      if (!rows.length) return 20;

      let rowHSum = 0;
      rows.forEach(row => { rowHSum += row.offsetHeight; });
      const avgRowH = Math.max(1, rowHSum / rows.length);

      const chromeH = totalH - avgRowH * sample.length;
      // Target 1100px (slightly under A4 96dpi height of 1123px) for safety margin
      const available = 1100 - chromeH;
      return Math.max(5, Math.floor(available / avgRowH));
    } finally {
      setMeasureChunk(null);
    }
  };

  useEffect(() => {
    setTitle("sidenav.hr.attendance");
  }, [setTitle]);

  // Load all attendance from cache for filtering (works both online and offline)
  useEffect(() => {
    const loadCachedAttendance = async () => {
      try {
        // Try to get all attendance from generic cache (per_page: 1000)
        const url = `${BASE_URL}/attendance`;
        const params = {
          page: 1,
          per_page: 1000,
        };

        const cachedData = await getGenericResponseCache(url, params);
        if (cachedData) {
          // Handle different response structures
          let attendanceData = [];

          if (Array.isArray(cachedData.data)) {
            attendanceData = cachedData.data;
          } else if (cachedData.data && Array.isArray(cachedData.data.data)) {
            attendanceData = cachedData.data.data;
          } else if (
            cachedData.data &&
            cachedData.data.data &&
            Array.isArray(cachedData.data.data)
          ) {
            attendanceData = cachedData.data.data;
          } else if (Array.isArray(cachedData)) {
            attendanceData = cachedData;
          }

          if (Array.isArray(attendanceData) && attendanceData.length > 0) {
            setAllCachedAttendance(attendanceData);
          } else {
            setAllCachedAttendance([]);
          }
        } else {
          setAllCachedAttendance([]);
        }
      } catch (error) {
        console.error("Error loading cached attendance:", error);
        setAllCachedAttendance([]);
      }
    };

    loadCachedAttendance();

    // Listen for online/offline status changes
    const handleOnlineStatusChange = () => {
      if (isOnline()) {
        // Clear cache when online to use fresh data
        setAllCachedAttendance([]);
      } else {
        // Reload cache when offline
        loadCachedAttendance();
      }
    };

    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);

    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
    };
  }, []);

  // Fetch all attendance if cache is empty and online
  useEffect(() => {
    const fetchAllAttendance = async () => {
      if (allCachedAttendance.length === 0 && isOnline()) {
        try {
          const params = {
            page: 1,
            per_page: 1000,
          };

          const result = await dispatch(getAttendance(params));
          if (getAttendance.fulfilled.match(result)) {
            const response = result.payload;
            let attendanceList = [];

            if (Array.isArray(response?.data?.data)) {
              attendanceList = response.data.data;
            } else if (Array.isArray(response?.data)) {
              attendanceList = response.data;
            } else if (Array.isArray(response)) {
              attendanceList = response;
            }

            if (attendanceList.length > 0) {
              setAllCachedAttendance(attendanceList);
            }
          }
        } catch (err) {
          console.error("Error fetching all attendance:", err);
        }
      }
    };

    fetchAllAttendance();
  }, [allCachedAttendance.length, dispatch]);

  const handleApplyPerPage = () => {
    // عند الضغط على تطبيق: نطبّق القيمة الجديدة ونرجع لأول صفحة
    const safeValue = toPositiveInteger(perPageInput, perPage);
    setPerPageInput(safeValue);
    setPerPage(safeValue);
    setCurrentPage(1);
  };

  // Fetch attendance with pagination
  const fetchAttendance = async (page = currentPage) => {
    try {
      // Use cached data for filtering if available (offline mode)
      const attendanceToFilter =
        allCachedAttendance.length > 0
          ? allCachedAttendance
          : allAttendance || [];

      // Apply filters locally if we have cached data OR if offline
      const shouldUseLocalFiltering =
        allCachedAttendance.length > 0 ||
        (!isOnline() && attendanceToFilter.length > 0);

      if (shouldUseLocalFiltering) {
        let filtered = [...attendanceToFilter];

        // Apply search filter
        if (searchTerm) {
          const lowerCaseSearchTerm = searchTerm.toLowerCase();
          filtered = filtered.filter(record => {
            const employee = record.employee;
            const employeeName = employee
              ? isRTL
                ? employee.name_ar || employee.name
                : employee.name_en || employee.name
              : "";
            const employeeCode = employee?.employee_code || "";
            return (
              employeeName.toLowerCase().includes(lowerCaseSearchTerm) ||
              employeeCode.toLowerCase().includes(lowerCaseSearchTerm) ||
              (record.notes &&
                record.notes.toLowerCase().includes(lowerCaseSearchTerm))
            );
          });
        }

        // Apply date filters
        if (fromDate) {
          filtered = filtered.filter(record => {
            const recordDate =
              record.work_date || record.date || record.attendance_date;
            if (!recordDate) return false;
            return new Date(recordDate) >= new Date(fromDate);
          });
        }

        if (toDate) {
          filtered = filtered.filter(record => {
            const recordDate =
              record.work_date || record.date || record.attendance_date;
            if (!recordDate) return false;
            return new Date(recordDate) <= new Date(toDate);
          });
        }

        // Apply sorting by sortField
        filtered.sort((a, b) => {
          if (sortField === "work_date") {
            const dA = new Date(
              a.work_date || a.date || a.attendance_date || 0,
            ).getTime();
            const dB = new Date(
              b.work_date || b.date || b.attendance_date || 0,
            ).getTime();
            return sortOrder === "asc" ? dA - dB : dB - dA;
          } else if (sortField === "employee") {
            const nA = (getEmployeeName(a.employee) || "").toLowerCase();
            const nB = (getEmployeeName(b.employee) || "").toLowerCase();
            if (sortOrder === "asc")
              return nA.localeCompare(nB, isRTL ? "ar" : "en");
            return nB.localeCompare(nA, isRTL ? "ar" : "en");
          } else if (sortField === "status") {
            const sA = a.status || "";
            const sB = b.status || "";
            if (sortOrder === "asc") return sA.localeCompare(sB);
            return sB.localeCompare(sA);
          } else {
            // default: id
            const idA = Number(a.id) || 0;
            const idB = Number(b.id) || 0;
            return sortOrder === "asc" ? idA - idB : idB - idA;
          }
        });

        // Apply pagination
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        const paginatedData = filtered.slice(startIndex, endIndex);

        setAllAttendance(paginatedData);

        // Set pagination
        setPagination({
          current_page: page,
          last_page: Math.ceil(filtered.length / perPage) || 1,
          per_page: perPage,
          total: filtered.length,
        });

        return;
      }

      // Online mode: fetch from API
      const params = {
        page: page,
        per_page: perPage,
        sort_by:
          sortField === "employee"
            ? "employee_name"
            : sortField === "work_date"
              ? "work_date"
              : sortField === "status"
                ? "status"
                : "id",
        sort_direction: sortOrder,
      };
      if (searchTerm) params.search = searchTerm;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const result = await dispatch(getAttendance(params));
      if (getAttendance.fulfilled.match(result)) {
        const response = result.payload;
        let attendanceList = [];
        let paginationData = null;

        // Handle different response structures
        if (Array.isArray(response?.data?.data)) {
          attendanceList = response.data.data;
          paginationData =
            response.data?.meta || response.data?.pagination || null;
        } else if (Array.isArray(response?.data)) {
          attendanceList = response.data;
          paginationData = response.meta || response.pagination || null;
        } else if (Array.isArray(response)) {
          attendanceList = response;
          paginationData = null;
        }

        setAllAttendance(attendanceList);

        // Normalize pagination
        if (paginationData) {
          setPagination({
            current_page:
              paginationData.current_page || paginationData.page || page,
            last_page:
              paginationData.last_page || paginationData.total_pages || 1,
            per_page: paginationData.per_page || perPage,
            total: paginationData.total || 0,
          });
        } else {
          setPagination(null);
        }
      }
    } catch (err) {
      console.error("Error fetching attendance:", err);
    }
  };

  const fetchCheckInEmployees = useCallback(
    async (page = checkInEmployeePage, search = checkInEmployeeSearchTerm) => {
      try {
        const payload = await dispatch(
          getEmployees({
            page,
            per_page: 10,
            search: search || undefined,
            language: i18n.language,
          }),
        ).unwrap();
        const { items, pagination: paginationMeta } =
          normalizeListResponse(payload);
        setCheckInEmployeeOptions(items);
        setCheckInEmployeePagination(paginationMeta);
      } catch (error) {
        toast.error(
          error ||
            t("attendance.modals.checkIn.messages.fetchEmployeesError", {
              defaultValue: "فشل جلب الموظفين",
            }),
          { rtl: isRTL },
        );
      }
    },
    [dispatch, t, i18n, isRTL],
  );

  const fetchCheckOutEmployees = useCallback(
    async (
      page = checkOutEmployeePage,
      search = checkOutEmployeeSearchTerm,
    ) => {
      try {
        const payload = await dispatch(
          getEmployees({
            page,
            per_page: 10,
            search: search || undefined,
            language: i18n.language,
          }),
        ).unwrap();
        const { items, pagination: paginationMeta } =
          normalizeListResponse(payload);
        setCheckOutEmployeeOptions(items);
        setCheckOutEmployeePagination(paginationMeta);
      } catch (error) {
        toast.error(
          error ||
            t("attendance.modals.checkOut.messages.fetchEmployeesError", {
              defaultValue: "فشل جلب الموظفين",
            }),
          { rtl: isRTL },
        );
      }
    },
    [dispatch, t, i18n, isRTL],
  );

  useEffect(() => {
    fetchAttendance(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, perPage, sortOrder, sortField]);

  const handlePageChange = newPage => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    if (checkInEmployeeDropdownOpen) {
      fetchCheckInEmployees(checkInEmployeePage, checkInEmployeeSearchTerm);
    }
  }, [checkInEmployeeDropdownOpen, checkInEmployeePage, fetchCheckInEmployees]);

  useEffect(() => {
    if (
      checkInEmployeeSearchTerm !== undefined &&
      checkInEmployeeDropdownOpen
    ) {
      const timeoutId = setTimeout(() => {
        setCheckInEmployeePage(1);
        fetchCheckInEmployees(1, checkInEmployeeSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [
    checkInEmployeeSearchTerm,
    checkInEmployeeDropdownOpen,
    fetchCheckInEmployees,
  ]);

  useEffect(() => {
    if (checkOutEmployeeDropdownOpen) {
      fetchCheckOutEmployees(checkOutEmployeePage, checkOutEmployeeSearchTerm);
    }
  }, [
    checkOutEmployeeDropdownOpen,
    checkOutEmployeePage,
    fetchCheckOutEmployees,
  ]);

  useEffect(() => {
    if (
      checkOutEmployeeSearchTerm !== undefined &&
      checkOutEmployeeDropdownOpen
    ) {
      const timeoutId = setTimeout(() => {
        setCheckOutEmployeePage(1);
        fetchCheckOutEmployees(1, checkOutEmployeeSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [
    checkOutEmployeeSearchTerm,
    checkOutEmployeeDropdownOpen,
    fetchCheckOutEmployees,
  ]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      const isClickInsideDropdown = event.target.closest(
        "[data-dropdown-container]",
      );
      const isClickOnDropdownToggle = event.target.closest(
        "[data-dropdown-toggle]",
      );

      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setCheckInEmployeeDropdownOpen(false);
        setCheckOutEmployeeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Search with debounce - trigger fetch when filters change
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchAttendance(1);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, fromDate, toDate, allCachedAttendance]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: isRTL });
        lastSuccessRef.current = { message: success, time: now };
      }
      dispatch(clearSuccess());
      setShowCheckInModal(false);
      setShowCheckOutModal(false);
      setCheckInData({ employee_id: "", check_in_notes: "" });
      setCheckOutData({ employee_id: "", check_out_notes: "" });
      setCheckInEmployeeSearchTerm("");
      setCheckInEmployeePage(1);
      setCheckOutEmployeeSearchTerm("");
      setCheckOutEmployeePage(1);
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, isRTL]);

  const handleCheckIn = async () => {
    if (!checkInData.employee_id) {
      toast.error(t("attendance.modals.checkIn.selectEmployeeError"), {
        rtl: isRTL,
      });
      return;
    }

    // Check for duplicate attendance on the same date
    const today = new Date().toISOString().split("T")[0];
    const recordsToCheck =
      allCachedAttendance.length > 0 ? allCachedAttendance : allAttendance;
    const alreadyCheckedIn = recordsToCheck.some(record => {
      const recordDate = (
        record.work_date ||
        record.date ||
        record.attendance_date ||
        ""
      ).split("T")[0];
      const recordEmployeeId = String(
        record.employee?.id || record.employee_id || "",
      );
      return (
        recordDate === today &&
        recordEmployeeId === String(checkInData.employee_id)
      );
    });

    if (alreadyCheckedIn) {
      toast.error(
        t("attendance.modals.checkIn.duplicateError", {
          defaultValue: "هذا الموظف سبق تسجيل حضوره اليوم",
        }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      await dispatch(checkIn(checkInData)).unwrap();
      fetchAttendance();
    } catch (error) {}
  };

  const handleCheckOut = async () => {
    if (!checkOutData.employee_id) {
      toast.error(t("attendance.modals.checkOut.selectEmployeeError"), {
        rtl: isRTL,
      });
      return;
    }
    try {
      await dispatch(checkOut(checkOutData)).unwrap();
      fetchAttendance();
    } catch (error) {}
  };

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US");
  };

  const formatTime = dateString => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Helper function to get employee name based on language
  const getEmployeeName = employee => {
    if (!employee) return "—";
    if (isRTL) {
      return employee.name_ar || employee.name || "—";
    } else {
      return employee.name_en || employee.name || "—";
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case "present":
        return "#AC2000";
      case "absent":
        return "#dc3545";
      case "late":
        return "#ffc107";
      default:
        return "#666";
    }
  };

  const getStatusText = status => {
    switch (status) {
      case "present":
        return t("attendance.status.present");
      case "absent":
        return t("attendance.status.absent");
      case "late":
        return t("attendance.status.late");
      default:
        return t("attendance.status.unspecified");
    }
  };

  // Handle print single attendance record
  const handlePrintAttendance = async record => {
    try {
      setAttendanceForPrint(record);
      setPdfType("single");
      await new Promise(resolve => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 300));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`attendance-pdf-${record.id}`);
      if (!element) {
        toast.error(
          t("attendance.print.error", {
            defaultValue: "خطأ في تحميل قالب الطباعة",
          }),
          { rtl: isRTL },
        );
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: clonedDoc => {
          const clonedElement = clonedDoc.getElementById(
            `attendance-pdf-${record.id}`,
          );
          if (clonedElement) {
            clonedElement.style.fontFamily =
              '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = "bold";
            clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            clonedElement.style.setProperty(
              "-webkit-font-smoothing",
              "antialiased",
            );
            clonedElement.style.setProperty(
              "-moz-osx-font-smoothing",
              "grayscale",
            );
            clonedElement.style.textRendering = "optimizeLegibility";

            const allElements = clonedElement.querySelectorAll("*");
            allElements.forEach(el => {
              if (el.style) {
                el.style.fontFamily =
                  '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = "bold";
                el.style.setProperty("-webkit-font-smoothing", "antialiased");
                el.style.setProperty("-moz-osx-font-smoothing", "grayscale");
                el.style.textRendering = "optimizeLegibility";
              }
            });
          }
        },
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL("image/png", 1.0);
      let imgWidth = pdfWidth;
      let imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Professional solution: If content slightly exceeds one page, scale it down to fit
      // This prevents extra blank pages while maintaining quality
      const tolerance = 10; // 10mm tolerance
      if (imgHeight > pdfHeight && imgHeight <= pdfHeight + tolerance) {
        // Scale down to fit exactly on one page
        const scaleFactor = pdfHeight / imgHeight;
        imgHeight = pdfHeight;
        imgWidth = pdfWidth * scaleFactor;

        // Center the scaled image
        const xOffset = (pdfWidth - imgWidth) / 2;
        pdf.addImage(
          imgData,
          "PNG",
          xOffset,
          0,
          imgWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      } else if (imgHeight <= pdfHeight) {
        // Fits perfectly on one page
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          imgWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      } else {
        // Content is too large, split across multiple pages
        let position = 0;
        while (position < imgHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          const remainingHeight = imgHeight - position;
          const pageHeight = Math.min(remainingHeight, pdfHeight);
          pdf.addImage(
            imgData,
            "PNG",
            0,
            -position,
            imgWidth,
            imgHeight,
            undefined,
            "FAST",
          );
          position += pdfHeight;
        }
      }

      const identifier =
        record.employee?.employee_code || record.employee?.name || record.id;
      pdf.save(
        `ACE_Attendance_${identifier}_${record.work_date || new Date().toISOString().split("T")[0]}.pdf`,
      );
    } catch (err) {
      console.error("Attendance PDF error:", err);
      toast.error(
        t("attendance.print.error", {
          defaultValue: "حدث خطأ أثناء إنشاء PDF",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setAttendanceForPrint(null);
      setPdfType(null);
      setIsSinglePreviewOpen(false);
      setAttendanceForSinglePreview(null);
    }
  };

  const totalPages = pagination?.last_page || 1;
  const currentPageSafe = pagination?.current_page || currentPage;
  const paginatedAttendance = React.useMemo(() => {
    if (!allAttendance || allAttendance.length === 0) return [];
    return [...allAttendance].sort((a, b) => {
      if (sortField === "work_date") {
        const dA = new Date(
          a.work_date || a.date || a.attendance_date || 0,
        ).getTime();
        const dB = new Date(
          b.work_date || b.date || b.attendance_date || 0,
        ).getTime();
        return sortOrder === "asc" ? dA - dB : dB - dA;
      } else if (sortField === "employee") {
        const nA = (a.employee?.name || "").toLowerCase();
        const nB = (b.employee?.name || "").toLowerCase();
        if (sortOrder === "asc")
          return nA.localeCompare(nB, isRTL ? "ar" : "en");
        return nB.localeCompare(nA, isRTL ? "ar" : "en");
      } else if (sortField === "status") {
        const sA = a.status || "";
        const sB = b.status || "";
        if (sortOrder === "asc") return sA.localeCompare(sB);
        return sB.localeCompare(sA);
      } else {
        const idA = Number(a.id) || 0;
        const idB = Number(b.id) || 0;
        return sortOrder === "asc" ? idA - idB : idB - idA;
      }
    });
  }, [allAttendance, sortOrder, sortField, isRTL]);

  // For export/print, we need all filtered data - fetch all if needed
  const [allFilteredAttendance, setAllFilteredAttendance] = useState([]);

  useEffect(() => {
    // Always fetch all records for export/print/preview (ignore current page)
    const fetchAll = async () => {
      const params = {
        page: 1,
        per_page: 10000,
        sort_by:
          sortField === "employee"
            ? "employee_name"
            : sortField === "work_date"
              ? "work_date"
              : sortField === "status"
                ? "status"
                : "id",
        sort_direction: sortOrder,
      };
      if (searchTerm) params.search = searchTerm;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const result = await dispatch(getAttendance(params));
      if (getAttendance.fulfilled.match(result)) {
        const response = result.payload;
        let attendanceList = [];
        if (Array.isArray(response?.data?.data)) {
          attendanceList = response.data.data;
        } else if (Array.isArray(response?.data)) {
          attendanceList = response.data;
        } else if (Array.isArray(response)) {
          attendanceList = response;
        }
        setAllFilteredAttendance(attendanceList);
      }
    };
    fetchAll();
  }, [searchTerm, fromDate, toDate, sortOrder, sortField, dispatch]);

  // Apply sort to the full list for export/print/preview
  const filteredAttendance = React.useMemo(() => {
    const source =
      allFilteredAttendance.length > 0 ? allFilteredAttendance : allAttendance;
    if (!source || source.length === 0) return [];
    return [...source].sort((a, b) => {
      if (sortField === "work_date") {
        const dA = new Date(
          a.work_date || a.date || a.attendance_date || 0,
        ).getTime();
        const dB = new Date(
          b.work_date || b.date || b.attendance_date || 0,
        ).getTime();
        return sortOrder === "asc" ? dA - dB : dB - dA;
      } else if (sortField === "employee") {
        const nA = (a.employee?.name || "").toLowerCase();
        const nB = (b.employee?.name || "").toLowerCase();
        if (sortOrder === "asc")
          return nA.localeCompare(nB, isRTL ? "ar" : "en");
        return nB.localeCompare(nA, isRTL ? "ar" : "en");
      } else if (sortField === "status") {
        const sA = a.status || "";
        const sB = b.status || "";
        if (sortOrder === "asc") return sA.localeCompare(sB);
        return sB.localeCompare(sA);
      } else {
        const idA = Number(a.id) || 0;
        const idB = Number(b.id) || 0;
        return sortOrder === "asc" ? idA - idB : idB - idA;
      }
    });
  }, [allFilteredAttendance, allAttendance, sortOrder, sortField, isRTL]);

  // Handle preview single attendance record
  const handlePreviewAttendance = async record => {
    setAttendanceForPrint(record);
    setPdfType("single");
    setAttendanceForSinglePreview(record);
    await new Promise(resolve => requestAnimationFrame(() => resolve()));
    await new Promise(resolve => setTimeout(resolve, 100));
    setIsSinglePreviewOpen(true);
  };

  // Handle export single attendance record to Excel
  const handleExportAttendanceToExcel = record => {
    try {
      const data = [
        {
          [t("common.serialNumber", { defaultValue: "#" })]: record.id,
          [t("attendance.tableHeaders.employee", { defaultValue: "الموظف" })]:
            getEmployeeName(record.employee) || "—",
          [t("attendance.tableHeaders.date", { defaultValue: "التاريخ" })]:
            formatDate(record.work_date || record.date || record.attendance_date),
          [t("attendance.tableHeaders.checkIn", { defaultValue: "وقت الدخول" })]:
            record.check_in ? formatTime(record.check_in) : "—",
          [t("attendance.tableHeaders.checkOut", { defaultValue: "وقت الخروج" })]:
            record.check_out ? formatTime(record.check_out) : "—",
          [t("attendance.tableHeaders.hours", { defaultValue: "ساعات العمل" })]:
            record.work_hours || record.hours || "—",
          [t("attendance.tableHeaders.status", { defaultValue: "الحالة" })]:
            record.status || "—",
          [t("attendance.tableHeaders.notes", { defaultValue: "الملاحظات" })]:
            record.notes || "—",
        },
      ];
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        t("attendance.title", { defaultValue: "Attendance" }),
      );
      const identifier =
        record.employee?.employee_code ||
        getEmployeeName(record.employee) ||
        record.id;
      const fileName = `ACE_Attendance_${identifier}_${record.work_date || new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(
        t("attendance.export.success", {
          defaultValue: "تم تصدير البيانات بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      toast.error(
        error.message ||
          t("attendance.export.error", {
            defaultValue: "فشل في تصدير البيانات",
          }),
        { rtl: isRTL },
      );
    }
  };

  // Handle preview all attendance records
  const handlePreviewAll = async () => {
    if (!filteredAttendance || filteredAttendance.length === 0) {
      toast.error(
        t("attendance.preview.noData", {
          defaultValue: "لا توجد بيانات للمعاينة",
        }),
        { rtl: isRTL },
      );
      return;
    }
    const rowsPerPage = await getDynamicRowsPerPage(filteredAttendance);
    const chunks = chunkArray(filteredAttendance, rowsPerPage);
    setPdfChunks(chunks);
    setPdfType("all");
    setIsPreviewOpen(true);
  };

  // Handle export all attendance to Excel
  const handleExportAllToExcel = async () => {
    try {
      // Use already-fetched filteredAttendance if available, otherwise fetch from API
      let allRecords =
        filteredAttendance && filteredAttendance.length > 0
          ? filteredAttendance
          : [];

      if (allRecords.length === 0) {
        const params = { page: 1, per_page: 10000 };
        if (searchTerm) params.search = searchTerm;
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;

        const result = await dispatch(getAttendance(params));
        if (getAttendance.fulfilled.match(result)) {
          const response = result.payload;
          if (Array.isArray(response?.data?.data))
            allRecords = response.data.data;
          else if (Array.isArray(response?.data)) allRecords = response.data;
          else if (Array.isArray(response)) allRecords = response;
        }
      }

      if (!allRecords || allRecords.length === 0) {
        toast.error(
          t("attendance.export.noData", {
            defaultValue: "لا توجد بيانات للتصدير",
          }),
          { rtl: isRTL },
        );
        return;
      }

      const data = allRecords.map((record, index) => ({
        [t("common.serialNumber", { defaultValue: "#" })]: index + 1,
        [t("attendance.tableHeaders.employee", { defaultValue: "الموظف" })]:
          getEmployeeName(record.employee) || record.employee_name || "—",
        [t("attendance.tableHeaders.date", { defaultValue: "التاريخ" })]:
          formatDate(record.work_date || record.date || record.attendance_date),
        [t("attendance.tableHeaders.checkIn", { defaultValue: "وقت الدخول" })]:
          record.check_in_time || "—",
        [t("attendance.tableHeaders.checkOut", { defaultValue: "وقت الخروج" })]:
          record.check_out_time || "—",
        [t("attendance.tableHeaders.hours", { defaultValue: "ساعات العمل" })]:
          record.work_hours || record.hours || "—",
        [t("attendance.tableHeaders.status", { defaultValue: "الحالة" })]:
          record.status || "—",
        [t("attendance.tableHeaders.notes", { defaultValue: "الملاحظات" })]:
          record.notes || "—",
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        t("attendance.title", { defaultValue: "Attendance" }),
      );
      const fileName = `${t("attendance.title", { defaultValue: "Attendance" })}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(
        t("attendance.export.success", {
          defaultValue: "تم تصدير البيانات بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      toast.error(
        error.message ||
          t("attendance.export.error", {
            defaultValue: "فشل في تصدير البيانات",
          }),
        { rtl: isRTL },
      );
    }
  };

  // Handle print all attendance records
  const handlePrintAll = async () => {
    if (!filteredAttendance || filteredAttendance.length === 0) {
      toast.error(
        t("attendance.print.noData", {
          defaultValue: "لا توجد بيانات للطباعة",
        }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      setIsGeneratingPdf(true);
      const rowsPerPage = await getDynamicRowsPerPage(filteredAttendance);
      const chunks = chunkArray(filteredAttendance, rowsPerPage);
      setPdfChunks(chunks);
      setPdfType("all");

      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise(resolve => setTimeout(resolve, 800));

      const pageWidthMm = 210;
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));
      let pdf = null;

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => setTimeout(resolve, 100));

        const element = document.getElementById(
          `attendance-pdf-all-${chunkIndex}`,
        );
        if (!element) {
          if (chunkIndex === 0) {
            toast.error(
              t("attendance.print.error", {
                defaultValue: "قالب الطباعة غير متوفر",
              }),
              { rtl: isRTL },
            );
            setIsGeneratingPdf(false);
            setPdfType(null);
            setPdfChunks([]);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          width: 794,
          windowWidth: 794,
          scrollX: 0,
          scrollY: 0,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `attendance-pdf-all-${chunkIndex}`,
            );
            if (clonedElement) {
              clonedElement.style.width = "794px";
              clonedElement.style.minWidth = "794px";
              clonedElement.style.maxWidth = "794px";
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = isRTL ? "rtl" : "ltr";
              clonedElement.style.setProperty(
                "-webkit-font-smoothing",
                "antialiased",
              );
              clonedElement.style.setProperty(
                "-moz-osx-font-smoothing",
                "grayscale",
              );
              clonedElement.style.textRendering = "optimizeLegibility";
              const allElements = clonedElement.querySelectorAll("*");
              allElements.forEach(el => {
                if (el.style) {
                  el.style.setProperty("-webkit-font-smoothing", "antialiased");
                  el.style.setProperty("-moz-osx-font-smoothing", "grayscale");
                  el.style.textRendering = "optimizeLegibility";
                }
              });
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const pageHeightMm = (canvas.height * pageWidthMm) / canvas.width;

        if (chunkIndex === 0) {
          pdf = new jsPDF("p", "mm", [pageWidthMm, pageHeightMm]);
        } else {
          pdf.addPage([pageWidthMm, pageHeightMm]);
        }

        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pageWidthMm,
          pageHeightMm,
          undefined,
          "FAST",
        );
      }

      pdf.save(`ACE_Attendance_${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success(
        t("attendance.print.success", { defaultValue: "تم تنزيل PDF بنجاح" }),
        { rtl: isRTL },
      );
    } catch (error) {
      console.error("Attendance PDF error:", error);
      toast.error(
        t("attendance.print.error", {
          defaultValue: "حدث خطأ أثناء إنشاء ملف الحضور",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "30px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "white",
            margin: 0,
            textAlign,
          }}
        >
          {t("attendance.title")}
        </h1>

        <div
          style={{
            display: "flex",
            gap: "15px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <label
              style={{ color: "white", fontSize: "14px", whiteSpace: "nowrap" }}
            >
              {t("common.perPage", { defaultValue: "عدد السجلات" })}:
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                onClick={() => {
                  const newValue = Math.max(
                    1,
                    toPositiveInteger(perPageInput, perPage) - 1,
                  );
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "6px 0 0 6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                step="1"
                value={perPageInput}
                onChange={e => {
                  setPerPageInput(toPositiveInteger(e.target.value, perPage));
                }}
                style={{
                  width: "70px",
                  padding: "6px 10px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderLeft: "none",
                  borderRight: "none",
                  borderRadius: 0,
                  color: "white",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              />
              <button
                onClick={() => {
                  const newValue = toPositiveInteger(perPageInput, perPage) + 1;
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: "#1b3350",
                  color: "white",
                  border: "1px solid #333",
                  padding: "6px 10px",
                  borderRadius: "0 6px 6px 0",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "32px",
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: "#AC2000",
                color: "white",
                border: "none",
                padding: "6px 12px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              {t("common.apply", { defaultValue: "تطبيق" })}
            </button>
          </div>
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              minWidth: 190,
              color: "white",
              fontSize: 13,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <span>
              {t("common.sortBy", {
                defaultValue: isRTL ? "ترتيب حسب" : "Sort by",
              })}
            </span>
            <select
              value={sortField}
              onChange={e => {
                setSortField(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: 8,
                color: "white",
                direction: isRTL ? "rtl" : "ltr",
              }}
            >
              <option value="id">
                {t("common.sortById", {
                  defaultValue: isRTL ? "رقم السجل" : "Record ID",
                })}
              </option>
              <option value="work_date">
                {t("attendance.table.workDate", {
                  defaultValue: isRTL ? "تاريخ العمل" : "Work Date",
                })}
              </option>
              <option value="employee">
                {t("attendance.table.employee", {
                  defaultValue: isRTL ? "الموظف" : "Employee",
                })}
              </option>
              <option value="status">
                {t("attendance.table.status", {
                  defaultValue: isRTL ? "الحالة" : "Status",
                })}
              </option>
            </select>
          </label>
          <SortOrderSelect
            value={sortOrder}
            onChange={val => {
              setSortOrder(val);
              setCurrentPage(1);
            }}
          />
          <button
            onClick={() => setShowCheckInModal(true)}
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaSignInAlt />
            {t("attendance.buttons.checkIn")}
          </button>

          <button
            onClick={() => setShowCheckOutModal(true)}
            style={{
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaSignOutAlt />
            {t("attendance.buttons.checkOut")}
          </button>

          <button
            onClick={handlePreviewAll}
            style={{
              backgroundColor: "#444",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaEye />{" "}
            {t("attendance.preview.all", { defaultValue: "عرض PDF للكل" })}
          </button>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: isGeneratingPdf ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
              opacity: isGeneratingPdf ? 0.6 : 1,
            }}
          >
            <FaPrint />
            {isGeneratingPdf
              ? t("attendance.print.generating", {
                  defaultValue: "جاري الطباعة...",
                })
              : t("attendance.print.all", { defaultValue: "طباعة PDF للكل" })}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaFileExcel />{" "}
            {t("attendance.export.all", { defaultValue: "تصدير Excel للكل" })}
          </button>
        </div>
      </div>

      {/***Search and Filters *****/}
      <div
        style={{
          marginBottom: "20px",
          backgroundColor: "#1b3350",
          padding: "20px",
          borderRadius: "12px",
          border: "1px solid #333",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "15px",
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <div style={{ position: "relative", flex: "1", minWidth: "250px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                color: "#ccc",
                fontWeight: "500",
                textAlign: isRTL ? "right" : "left",
                direction: dir,
              }}
            >
              {t("attendance.searchPlaceholder")}
            </label>
            <div style={{ position: "relative" }}>
              <FaSearch
                style={{
                  position: "absolute",
                  [isRTL ? "right" : "left"]: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#666",
                  fontSize: "16px",
                  zIndex: 1,
                }}
              />
              <input
                type="text"
                placeholder={t("attendance.searchPlaceholder")}
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page when searching
                }}
                style={{
                  width: "100%",
                  padding: isRTL
                    ? "12px 45px 12px 15px"
                    : "12px 15px 12px 45px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  textAlign,
                  direction: dir,
                }}
              />
            </div>
          </div>
          <div style={{ minWidth: "150px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                color: "#ccc",
                fontWeight: "500",
                textAlign: isRTL ? "right" : "left",
                direction: dir,
              }}
            >
              {t("attendance.filters.fromDate")}
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={e => {
                setFromDate(e.target.value);
                setCurrentPage(1); // Reset to first page when changing date
              }}
              style={{
                width: "100%",
                padding: "12px 15px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={{ minWidth: "150px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                color: "#ccc",
                fontWeight: "500",
                textAlign: isRTL ? "right" : "left",
                direction: dir,
              }}
            >
              {t("attendance.filters.toDate")}
            </label>
            <input
              type="date"
              value={toDate}
              onChange={e => {
                setToDate(e.target.value);
                setCurrentPage(1); // Reset to first page when changing date
              }}
              style={{
                width: "100%",
                padding: "12px 15px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      </div>

      {/* Attendance Table */}
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #333",
        }}
      >
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px",
              color: "white",
            }}
          >
            <div>{t("attendance.loading")}</div>
          </div>
        ) : paginatedAttendance && paginatedAttendance.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "1300px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#1a1f2e" }}>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                      borderBottom: "1px solid #333",
                    }}
                  >
                    {t("attendance.table.number")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                      borderBottom: "1px solid #333",
                    }}
                  >
                    {t("attendance.table.employee")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                      borderBottom: "1px solid #333",
                    }}
                  >
                    {t("attendance.table.workDate")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                      borderBottom: "1px solid #333",
                    }}
                  >
                    {t("attendance.table.checkInTime")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                      borderBottom: "1px solid #333",
                    }}
                  >
                    {t("attendance.table.checkOutTime")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                      borderBottom: "1px solid #333",
                    }}
                  >
                    {t("attendance.table.notes")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                      borderBottom: "1px solid #333",
                    }}
                  >
                    {t("attendance.table.status")}
                  </th>
                  <th
                    style={{
                      padding: "15px",
                      textAlign: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                      borderBottom: "1px solid #333",
                    }}
                  >
                    {t("attendance.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedAttendance && paginatedAttendance.length > 0 ? (
                  paginatedAttendance.map((record, index) => {
                    const total = pagination?.total || 0;
                    const recordIndex =
                      sortOrder === "desc"
                        ? total - (currentPageSafe - 1) * perPage - index
                        : (currentPageSafe - 1) * perPage + index + 1;

                    return (
                      <tr
                        key={record.id}
                        style={{
                          borderBottom: "2px solid #444",
                          borderTop: index === 0 ? "2px solid #444" : "none",
                        }}
                      >
                        <td
                          style={{
                            padding: "15px",
                            textAlign: "center",
                            color: "white",
                          }}
                        >
                          {recordIndex}
                        </td>
                        <td
                          style={{
                            padding: "15px",
                            textAlign: "center",
                            color: "white",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: "bold" }}>
                              {getEmployeeName(record.employee)}
                            </div>
                            <div style={{ fontSize: "12px", color: "#ccc" }}>
                              {record.employee?.employee_code}
                            </div>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "15px",
                            textAlign: "center",
                            color: "white",
                          }}
                        >
                          {formatDate(record.work_date)}
                        </td>
                        <td
                          style={{
                            padding: "15px",
                            textAlign: "center",
                            color: "white",
                          }}
                        >
                          {record.check_in ? formatTime(record.check_in) : "-"}
                        </td>
                        <td
                          style={{
                            padding: "15px",
                            textAlign: "center",
                            color: "white",
                          }}
                        >
                          {record.check_out
                            ? formatTime(record.check_out)
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "15px",
                            textAlign: "center",
                            color: "white",
                          }}
                        >
                          <div style={{ textAlign, fontSize: "12px" }}>
                            {record.notes ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "8px",
                                }}
                              >
                                {record.notes.split("\n").map((note, index) => {
                                  const isCheckout = note.includes("Checkout");
                                  const noteText = isCheckout
                                    ? note.replace("Checkout Notes: ", "")
                                    : note;
                                  return (
                                    <div
                                      key={index}
                                      className="attendance-note-card"
                                      style={{
                                        position: "relative",
                                        marginBottom: "8px",
                                        padding: "12px 16px",
                                        backgroundColor: isCheckout
                                          ? "rgba(220, 53, 69, 0.1)"
                                          : "rgba(172, 32, 0, 0.1)",
                                        border: `2px solid ${isCheckout ? "#dc3545" : "#AC2000"}`,
                                        borderRadius: "12px",
                                        fontSize: "12px",
                                        boxShadow:
                                          "0 2px 8px rgba(0, 0, 0, 0.3)",
                                        backdropFilter: "blur(10px)",
                                      }}
                                    >
                                      {/* أيقونة */}
                                      <div
                                        className="note-icon"
                                        style={{
                                          position: "absolute",
                                          top: "-8px",
                                          right: "12px",
                                          width: "20px",
                                          height: "20px",
                                          backgroundColor: isCheckout
                                            ? "#dc3545"
                                            : "#AC2000",
                                          borderRadius: "50%",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: "10px",
                                          color: "white",
                                          fontWeight: "bold",
                                          boxShadow:
                                            "0 2px 4px rgba(0, 0, 0, 0.3)",
                                          transition: "transform 0.3s ease",
                                        }}
                                      >
                                        {isCheckout ? "↲" : "✓"}
                                      </div>

                                      {/* نوع الملاحظة */}
                                      <div
                                        style={{
                                          color: isCheckout
                                            ? "#dc3545"
                                            : "#AC2000",
                                          fontWeight: "bold",
                                          marginBottom: "4px",
                                          fontSize: "11px",
                                          textTransform: "uppercase",
                                          letterSpacing: "0.5px",
                                        }}
                                      >
                                        {isCheckout
                                          ? t("attendance.notes.checkOutNote")
                                          : t("attendance.notes.checkInNote")}
                                      </div>

                                      {/* نص الملاحظة */}
                                      <div
                                        style={{
                                          color: "white",
                                          lineHeight: "1.4",
                                          wordBreak: "break-word",
                                          fontSize: "11px",
                                        }}
                                      >
                                        {noteText}
                                      </div>

                                      {/* خط زمني */}
                                      <div
                                        className="note-timeline"
                                        style={{
                                          position: "absolute",
                                          bottom: "0",
                                          left: "0",
                                          right: "0",
                                          height: "2px",
                                          background: `linear-gradient(90deg, ${isCheckout ? "#dc3545" : "#AC2000"}, transparent)`,
                                          borderRadius: "0 0 12px 12px",
                                          transition: "opacity 0.3s ease",
                                        }}
                                      ></div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div
                                style={{
                                  padding: "20px",
                                  textAlign: "center",
                                  color: "#666",
                                  fontSize: "14px",
                                  fontStyle: "italic",
                                }}
                              >
                                <div
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    margin: "0 auto 8px",
                                    backgroundColor: "#333",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "18px",
                                  }}
                                >
                                  📝
                                </div>
                                {t("attendance.notes.noNotes")}
                              </div>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "15px",
                            textAlign: "center",
                          }}
                        >
                          <span
                            style={{
                              backgroundColor: getStatusColor(record.status),
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              minWidth: "80px",
                              display: "inline-block",
                            }}
                          >
                            {getStatusText(record.status)}
                          </span>
                        </td>
                        <td style={{ padding: "15px" }}>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "center",
                              flexWrap: "nowrap",
                            }}
                          >
                            <button
                              onClick={() => handlePreviewAttendance(record)}
                              style={{
                                backgroundColor: "#666",
                                color: "white",
                                border: "none",
                                padding: "8px 10px",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "13px",
                                flexShrink: 0,
                              }}
                              title={t("attendance.preview.single", {
                                defaultValue: "معاينة",
                              })}
                            >
                              <FaEye />
                            </button>
                            <button
                              onClick={() => handlePrintAttendance(record)}
                              disabled={isGeneratingPdf}
                              style={{
                                backgroundColor: isGeneratingPdf
                                  ? "#666"
                                  : "#17a2b8",
                                color: "white",
                                border: "none",
                                padding: "8px 10px",
                                borderRadius: "6px",
                                cursor: isGeneratingPdf
                                  ? "not-allowed"
                                  : "pointer",
                                fontSize: "13px",
                                opacity: isGeneratingPdf ? 0.6 : 1,
                                flexShrink: 0,
                              }}
                              title={t("attendance.print.single", {
                                defaultValue: "طباعة",
                              })}
                            >
                              <FaPrint />
                            </button>
                            <button
                              onClick={() => handleExportAttendanceToExcel(record)}
                              style={{
                                backgroundColor: "#28a745",
                                color: "white",
                                border: "none",
                                padding: "8px 10px",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "13px",
                                flexShrink: 0,
                              }}
                              title={t("attendance.export.single", {
                                defaultValue: "تصدير Excel",
                              })}
                            >
                              <FaFileExcel />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      style={{
                        textAlign: "center",
                        color: "white",
                        padding: "20px",
                      }}
                    >
                      {t("attendance.noRecords", {
                        defaultValue: "لا توجد سجلات",
                      })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
            }}
          >
            {t("attendance.noRecords", { defaultValue: "لا توجد سجلات" })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              padding: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              borderTop: "1px solid #333",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => handlePageChange(Math.max(1, currentPageSafe - 1))}
              disabled={currentPageSafe === 1}
              style={{
                backgroundColor: currentPageSafe === 1 ? "#333" : "#AC2000",
                color: "white",
                border: "none",
                padding: "8px 12px",
                borderRadius: "6px",
                cursor: currentPageSafe === 1 ? "not-allowed" : "pointer",
                fontSize: "14px",
              }}
            >
              {t("common.previous", { defaultValue: "السابق" })}
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                style={{
                  backgroundColor:
                    page === currentPageSafe ? "#AC2000" : "#1a1f2e",
                  color: "white",
                  border: "1px solid #333",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: page === currentPageSafe ? "bold" : "normal",
                }}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() =>
                handlePageChange(Math.min(totalPages, currentPageSafe + 1))
              }
              disabled={currentPageSafe === totalPages}
              style={{
                backgroundColor:
                  currentPageSafe === totalPages ? "#333" : "#AC2000",
                color: "white",
                border: "none",
                padding: "8px 12px",
                borderRadius: "6px",
                cursor:
                  currentPageSafe === totalPages ? "not-allowed" : "pointer",
                fontSize: "14px",
              }}
            >
              {t("common.next", { defaultValue: "التالي" })}
            </button>
          </div>
        )}
      </div>

      {/* Check In Modal */}
      {showCheckInModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#1b3350",
              padding: "30px",
              borderRadius: "12px",
              border: "1px solid #333",
              maxWidth: "500px",
              width: "90%",
            }}
          >
            <h3
              style={{
                color: "white",
                marginBottom: "20px",
                fontSize: "18px",
                textAlign: isRTL ? "right" : "left",
                direction: dir,
              }}
            >
              {t("attendance.modals.checkIn.title")}
            </h3>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "white",
                  fontWeight: "500",
                  textAlign: isRTL ? "right" : "left",
                  direction: dir,
                }}
              >
                {t("attendance.modals.checkIn.employeeLabel")}
              </label>
              <div style={{ position: "relative" }} data-dropdown-container>
                <div
                  data-dropdown-toggle
                  onClick={() => setCheckInEmployeeDropdownOpen(prev => !prev)}
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    direction: dir,
                    textAlign,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <span>
                    {checkInData.employee_id
                      ? (() => {
                          const selected = checkInEmployeeOptions.find(
                            e =>
                              String(e.id) === String(checkInData.employee_id),
                          );
                          return selected
                            ? `${getEmployeeName(selected)} - ${selected.employee_code}`
                            : t("attendance.modals.checkIn.selectEmployee");
                        })()
                      : t("attendance.modals.checkIn.selectEmployee")}
                  </span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                <DropdownWithSearch
                  isOpen={checkInEmployeeDropdownOpen}
                  searchTerm={checkInEmployeeSearchTerm}
                  onSearchChange={setCheckInEmployeeSearchTerm}
                  searchInputRef={checkInEmployeeSearchInputRef}
                  items={checkInEmployeeOptions}
                  pagination={checkInEmployeePagination}
                  onPageChange={setCheckInEmployeePage}
                  onClose={() => setCheckInEmployeeDropdownOpen(false)}
                  onSelect={item => {
                    setCheckInData(prev => ({
                      ...prev,
                      employee_id: String(item.id),
                    }));
                    setCheckInEmployeeDropdownOpen(false);
                    setCheckInEmployeeSearchTerm("");
                    setCheckInEmployeePage(1);
                  }}
                  label={t("attendance.modals.checkIn.employeeLabel")}
                  isRTL={isRTL}
                  i18n={i18n}
                  modalType="checkIn"
                />
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "white",
                  fontWeight: "500",
                  textAlign: isRTL ? "right" : "left",
                  direction: dir,
                }}
              >
                {t("attendance.modals.checkIn.notesLabel")}
              </label>
              <textarea
                value={checkInData.check_in_notes}
                onChange={e =>
                  setCheckInData({
                    ...checkInData,
                    check_in_notes: e.target.value,
                  })
                }
                placeholder={t("attendance.modals.checkIn.notesPlaceholder")}
                rows="3"
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  resize: "vertical",
                  textAlign,
                  direction: dir,
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                flexDirection: isRTL ? "row" : "row-reverse",
              }}
            >
              <button
                onClick={() => setShowCheckInModal(false)}
                style={{
                  backgroundColor: "#666",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {t("attendance.modals.checkIn.cancel")}
              </button>
              <button
                onClick={handleCheckIn}
                disabled={isLoading}
                style={{
                  backgroundColor: isLoading ? "#666" : "#AC2000",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                {isLoading
                  ? t("attendance.modals.checkIn.submitting")
                  : t("attendance.modals.checkIn.submit")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Check Out Modal */}
      {showCheckOutModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#1b3350",
              padding: "30px",
              borderRadius: "12px",
              border: "1px solid #333",
              maxWidth: "500px",
              width: "90%",
            }}
          >
            <h3
              style={{
                color: "white",
                marginBottom: "20px",
                fontSize: "18px",
                textAlign: isRTL ? "right" : "left",
                direction: dir,
              }}
            >
              {t("attendance.modals.checkOut.title")}
            </h3>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "white",
                  fontWeight: "500",
                  textAlign: isRTL ? "right" : "left",
                  direction: dir,
                }}
              >
                {t("attendance.modals.checkOut.employeeLabel")}
              </label>
              <div style={{ position: "relative" }} data-dropdown-container>
                <div
                  data-dropdown-toggle
                  onClick={() => setCheckOutEmployeeDropdownOpen(prev => !prev)}
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    direction: dir,
                    textAlign,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <span>
                    {checkOutData.employee_id
                      ? (() => {
                          const selected = checkOutEmployeeOptions.find(
                            e =>
                              String(e.id) === String(checkOutData.employee_id),
                          );
                          return selected
                            ? `${getEmployeeName(selected)} - ${selected.employee_code}`
                            : t("attendance.modals.checkOut.selectEmployee");
                        })()
                      : t("attendance.modals.checkOut.selectEmployee")}
                  </span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                <DropdownWithSearch
                  isOpen={checkOutEmployeeDropdownOpen}
                  searchTerm={checkOutEmployeeSearchTerm}
                  onSearchChange={setCheckOutEmployeeSearchTerm}
                  searchInputRef={checkOutEmployeeSearchInputRef}
                  items={checkOutEmployeeOptions}
                  pagination={checkOutEmployeePagination}
                  onPageChange={setCheckOutEmployeePage}
                  onClose={() => setCheckOutEmployeeDropdownOpen(false)}
                  onSelect={item => {
                    setCheckOutData(prev => ({
                      ...prev,
                      employee_id: String(item.id),
                    }));
                    setCheckOutEmployeeDropdownOpen(false);
                    setCheckOutEmployeeSearchTerm("");
                    setCheckOutEmployeePage(1);
                  }}
                  label={t("attendance.modals.checkOut.employeeLabel")}
                  isRTL={isRTL}
                  i18n={i18n}
                  modalType="checkOut"
                />
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "white",
                  fontWeight: "500",
                  textAlign: isRTL ? "right" : "left",
                  direction: dir,
                }}
              >
                {t("attendance.modals.checkOut.notesLabel")}
              </label>
              <textarea
                value={checkOutData.check_out_notes}
                onChange={e =>
                  setCheckOutData({
                    ...checkOutData,
                    check_out_notes: e.target.value,
                  })
                }
                placeholder={t("attendance.modals.checkOut.notesPlaceholder")}
                rows="3"
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  resize: "vertical",
                  textAlign,
                  direction: dir,
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                flexDirection: isRTL ? "row" : "row-reverse",
              }}
            >
              <button
                onClick={() => setShowCheckOutModal(false)}
                style={{
                  backgroundColor: "#666",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {t("attendance.modals.checkOut.cancel")}
              </button>
              <button
                onClick={handleCheckOut}
                disabled={isLoading}
                style={{
                  backgroundColor: isLoading ? "#666" : "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                {isLoading
                  ? t("attendance.modals.checkOut.submitting")
                  : t("attendance.modals.checkOut.submit")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Templates */}
      {/* Single Attendance Record Print Template */}
      {attendanceForPrint && pdfType === "single" && (
        <div
          id={`attendance-pdf-${attendanceForPrint.id}`}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            width: "794px",
            minHeight: "1123px",
            height: "auto",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            padding: "40px",
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <div
            style={{
              border: "5px solid #000",
              padding: "3px",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              minHeight: "1043px",
              height: "auto",
              overflow: "visible",
            }}
          >
            <div
              style={{
                border: "2px solid #000",
                minHeight: "1033px",
                height: "1033px",
                padding: "18px 18px 10px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                textRendering: "optimizeLegibility",
                direction: isRTL ? "rtl" : "ltr",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: "95%",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: "100%",
                }}
              >
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    marginBottom: "8px",
                    borderBottom: "4px double #000",
                    paddingBottom: "8px",
                    flexShrink: 0,
                  }}
                  crossOrigin="anonymous"
                />

                <div style={{ textAlign: "center", padding: "6px 0 8px", borderBottom: "1px solid #ddd", marginBottom: "4px", flexShrink: 0, direction: isRTL ? "rtl" : "ltr" }}>
                  <div style={{ fontSize: "16px", fontWeight: "900", color: "#000080" }}>{isRTL ? "تقرير الحضور" : "Attendance Report"}</div>
                  <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>ID: #{attendanceForPrint.id}</div>
                </div>

                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "visible",
                    minHeight: 0,
                    paddingBottom: "120px",
                  }}
                >
                  <div
                    style={{
                      marginTop: "12px",
                      border: "2px solid #000",
                      borderRadius: "4px",
                      overflow: "hidden",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    {[
                      {
                        label: t("attendance.pdfTemplate.employeeName"),
                        value:
                          getEmployeeName(attendanceForPrint.employee) || "—",
                      },
                      {
                        label: t("attendance.pdfTemplate.employeeCode"),
                        value:
                          attendanceForPrint.employee?.employee_code || "—",
                      },
                      {
                        label: t("attendance.pdfTemplate.workDate"),
                        value: formatDate(attendanceForPrint.work_date) || "—",
                        accent: true,
                      },
                      {
                        label: t("attendance.pdfTemplate.checkInTime"),
                        value: attendanceForPrint.check_in
                          ? formatTime(attendanceForPrint.check_in)
                          : "—",
                      },
                      {
                        label: t("attendance.pdfTemplate.checkOutTime"),
                        value: attendanceForPrint.check_out
                          ? formatTime(attendanceForPrint.check_out)
                          : "—",
                      },
                      {
                        label: t("attendance.pdfTemplate.status"),
                        value: getStatusText(attendanceForPrint.status),
                        accent: true,
                      },
                      ...(attendanceForPrint.notes
                        ? [
                            {
                              label: t("attendance.pdfTemplate.notes"),
                              value: attendanceForPrint.notes
                                .split("\n")
                                .join(" | "),
                            },
                          ]
                        : []),
                    ]
                      .filter(row => row.value && row.value !== "—")
                      .map(row => (
                        <div
                          key={row.label}
                          style={{
                            display: "flex",
                            borderBottom: "1px solid #000",
                            minHeight: "38px",
                            flexDirection: isRTL ? "row-reverse" : "row",
                          }}
                        >
                          <div
                            style={{
                              width: "30%",
                              [isRTL ? "borderRight" : "borderLeft"]:
                                "1px solid #000",
                              backgroundColor: "rgba(227, 242, 253, 0.4)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: isRTL ? "flex-end" : "flex-start",
                              textAlign: isRTL ? "right" : "left",
                              fontWeight: "700",
                              fontSize: "14px",
                              direction: isRTL ? "rtl" : "ltr",
                              color: "#1976d2",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              fontWeight: "bold",
                              padding: "0 10px",
                            }}
                          >
                            {row.label}
                          </div>
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: isRTL ? "flex-end" : "flex-start",
                              textAlign: isRTL ? "right" : "left",
                              fontSize: "15px",
                              fontWeight: row.accent ? "700" : "500",
                              color: row.accent ? "#0057c2" : "#000",
                              direction: isRTL ? "rtl" : "ltr",
                              padding: "0 10px",
                              backgroundColor: "rgba(255, 255, 255, 0.3)",
                              fontFamily:
                                '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              fontWeight: "bold",
                            }}
                          >
                            {row.value}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "18px",
                    right: "18px",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                        borderTop: "4px double #000",
                        paddingTop: "8px",
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Measurement Template – off-screen, used only to calculate dynamic rows per page */}
      {measureChunk && measureChunk.length > 0 && (
        <AllAttendancePrintTemplate
          records={measureChunk}
          chunkIndex={0}
          measureId="attendance-pdf-measure"
          allRecordsList={measureChunk}
          isRTL={isRTL}
          t={t}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
          getEmployeeName={getEmployeeName}
          formatDate={formatDate}
          formatTime={formatTime}
          getStatusText={getStatusText}
        />
      )}

      {/* All Attendance Records Print Template */}
      {pdfType === "all" &&
        pdfChunks &&
        pdfChunks.length > 0 &&
        pdfChunks.map((chunk, chunkIndex) => (
          <AllAttendancePrintTemplate
            key={chunkIndex}
            records={chunk}
            chunkIndex={chunkIndex}
            allRecordsList={filteredAttendance}
            isRTL={isRTL}
            t={t}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            getEmployeeName={getEmployeeName}
            formatDate={formatDate}
            formatTime={formatTime}
            getStatusText={getStatusText}
          />
        ))}

      {/* Single Attendance Preview Modal */}
      <PdfPreviewModal
        isOpen={isSinglePreviewOpen && !!attendanceForSinglePreview}
        title={t("attendance.title", { defaultValue: "الحضور" })}
        onClose={() => {
          setIsSinglePreviewOpen(false);
          setAttendanceForSinglePreview(null);
          setAttendanceForPrint(null);
          setPdfType(null);
        }}
        onPrint={() => {
          if (attendanceForSinglePreview) {
            handlePrintAttendance(attendanceForSinglePreview);
          }
        }}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px", alignItems: "center" }}>
          {attendanceForSinglePreview && (
            <div
              style={{
                width: "794px",
                minWidth: "794px",
                maxWidth: "794px",
                height: "auto",
                backgroundColor: "#ffffff",
                color: "#000000",
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                padding: "40px",
                boxSizing: "border-box",
                boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
              }}
            >
              <div style={{ border: "5px solid #000", padding: "3px", backgroundColor: "#ffffff", boxSizing: "border-box", width: "100%" }}>
                <div
                  style={{
                    border: "2px solid #000",
                    padding: "18px 18px 10px",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                    direction: isRTL ? "rtl" : "ltr",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: "95%",
                      backgroundPosition: "center center",
                      backgroundRepeat: "no-repeat",
                      opacity: 0.08,
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  />
                  <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column" }}>
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{ width: "100%", height: "auto", objectFit: "contain", marginBottom: "8px", borderBottom: "4px double #000", paddingBottom: "8px", flexShrink: 0 }}
                      crossOrigin="anonymous"
                    />
                    <div style={{ textAlign: "center", padding: "6px 0 8px", borderBottom: "1px solid #ddd", marginBottom: "4px", flexShrink: 0, direction: isRTL ? "rtl" : "ltr" }}>
                      <div style={{ fontSize: "16px", fontWeight: "900", color: "#000080" }}>{isRTL ? "تقرير الحضور" : "Attendance Report"}</div>
                      <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>ID: #{attendanceForSinglePreview.id}</div>
                    </div>
                    <div style={{ marginTop: "12px", border: "2px solid #000", borderRadius: "4px", overflow: "hidden", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: "bold", backgroundColor: "rgba(255,255,255,0.3)" }}>
                      {[
                        { label: t("attendance.pdfTemplate.employeeName"), value: getEmployeeName(attendanceForSinglePreview.employee) || "—" },
                        { label: t("attendance.pdfTemplate.employeeCode"), value: attendanceForSinglePreview.employee?.employee_code || "—" },
                        { label: t("attendance.pdfTemplate.workDate"), value: formatDate(attendanceForSinglePreview.work_date) || "—", accent: true },
                        { label: t("attendance.pdfTemplate.checkInTime"), value: attendanceForSinglePreview.check_in ? formatTime(attendanceForSinglePreview.check_in) : "—" },
                        { label: t("attendance.pdfTemplate.checkOutTime"), value: attendanceForSinglePreview.check_out ? formatTime(attendanceForSinglePreview.check_out) : "—" },
                        { label: t("attendance.pdfTemplate.status"), value: getStatusText(attendanceForSinglePreview.status), accent: true },
                        ...(attendanceForSinglePreview.notes ? [{ label: t("attendance.pdfTemplate.notes"), value: attendanceForSinglePreview.notes.split("\n").join(" | ") }] : []),
                      ]
                        .filter(row => row.value && row.value !== "—")
                        .map(row => (
                          <div key={row.label} style={{ display: "flex", borderBottom: "1px solid #000", minHeight: "38px", flexDirection: isRTL ? "row-reverse" : "row" }}>
                            <div style={{ width: "30%", [isRTL ? "borderRight" : "borderLeft"]: "1px solid #000", backgroundColor: "rgba(227,242,253,0.4)", display: "flex", alignItems: "center", justifyContent: isRTL ? "flex-end" : "flex-start", textAlign: isRTL ? "right" : "left", fontWeight: "700", fontSize: "14px", direction: isRTL ? "rtl" : "ltr", color: "#1976d2", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', padding: "0 10px" }}>
                              {row.label}
                            </div>
                            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: isRTL ? "flex-end" : "flex-start", textAlign: isRTL ? "right" : "left", fontSize: "15px", fontWeight: row.accent ? "700" : "500", color: row.accent ? "#0057c2" : "#000", direction: isRTL ? "rtl" : "ltr", padding: "0 10px", backgroundColor: "rgba(255,255,255,0.3)", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                              {row.value}
                            </div>
                          </div>
                        ))}
                    </div>
                    <div style={{ marginTop: "20px" }}>
                      <img
                        src={`${publicUrl}/FooterAce.png`}
                        alt="Footer"
                        style={{ width: "100%", height: "auto", objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",
}}
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </PdfPreviewModal>

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={
          isPreviewOpen && filteredAttendance && filteredAttendance.length > 0
        }
        title={t("attendance.title", { defaultValue: "الحضور" })}
        onClose={() => {
          setIsPreviewOpen(false);
          setPdfType(null);
          setPdfChunks([]);
        }}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          }}
        >
          {pdfChunks &&
            pdfChunks.length > 0 &&
            pdfChunks.map((chunk, chunkIdx) => (
              <div
                key={chunkIdx}
                style={{
                  width: "794px",
                  height: "auto",
                  backgroundColor: "#ffffff",
                  color: "#000000",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  padding: "40px",
                  boxSizing: "border-box",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                }}
              >
                <div
                  style={{
                    border: "5px solid #000",
                    padding: "3px",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      border: "2px solid #000",
                      padding: "18px 18px 10px",
                      position: "relative",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                      direction: isRTL ? "rtl" : "ltr",
                      backgroundColor: "#ffffff",
                      boxSizing: "border-box",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundImage: `url(${headerLogo})`,
                        backgroundSize: "60%",
                        backgroundPosition: "center top",
                        backgroundRepeat: "no-repeat",
                        opacity: 0.05,
                        zIndex: 0,
                        pointerEvents: "none",
                      }}
                    />
                    <div style={{ position: "relative", zIndex: 1 }}>
                      <img
                        src={`${publicUrl}/HeaderAce.png`}
                        alt="Header"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                          marginBottom: "8px",
                          borderBottom: "4px double #000",
                          paddingBottom: "8px",
                          display: "block",
                        }}
                        crossOrigin="anonymous"
                      />
                      <div style={{ textAlign: "center", padding: "6px 0 8px", borderBottom: "1px solid #ddd", marginBottom: "4px", direction: isRTL ? "rtl" : "ltr" }}>
                        <div style={{ fontSize: "16px", fontWeight: "900", color: "#000080" }}>{isRTL ? "تقرير الحضور" : "Attendance Report"}</div>
                        <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>{isRTL ? "تاريخ التقرير" : "Report Date"}: {new Date().toLocaleDateString("en-US")}</div>
                      </div>
                      <div
                        style={{
                          marginTop: "12px",
                          direction: isRTL ? "rtl" : "ltr",
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "11px",
                            border: "1px solid #000",
                            fontFamily: '"Times New Roman", "Times", serif',
                            backgroundColor: "rgba(255,255,255,0.7)",
                            tableLayout: "fixed",
                          }}
                        >
                          <colgroup>
                            <col style={{ width: "3%" }} />
                            <col style={{ width: "28%" }} />
                            <col style={{ width: "10%" }} />
                            <col style={{ width: "13%" }} />
                            <col style={{ width: "13%" }} />
                            <col style={{ width: "13%" }} />
                            <col style={{ width: "20%" }} />
                          </colgroup>
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "rgba(238,242,247,0.7)",
                              }}
                            >
                              {[
                                "#",
                                t("attendance.tableHeaders.employee", {
                                  defaultValue: "الموظف",
                                }),
                                t("attendance.tableHeaders.employeeCode", {
                                  defaultValue: "كود",
                                }),
                                t("attendance.tableHeaders.date", {
                                  defaultValue: "التاريخ",
                                }),
                                t("attendance.tableHeaders.checkIn", {
                                  defaultValue: "دخول",
                                }),
                                t("attendance.tableHeaders.checkOut", {
                                  defaultValue: "خروج",
                                }),
                                t("attendance.tableHeaders.status", {
                                  defaultValue: "الحالة",
                                }),
                              ].map(header => (
                                <th
                                  key={header}
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px 2px",
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                    fontWeight: "700",
                                    backgroundColor: "rgba(238,242,247,0.7)",
                                    overflow: "hidden",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {chunk.map((record, idx) => {
                              const globalIdx = filteredAttendance.findIndex(
                                r => String(r?.id) === String(record?.id),
                              );
                              const total = filteredAttendance.length;
                              const serialNumber =
                                globalIdx >= 0
                                  ? sortOrder === "desc"
                                    ? total - globalIdx
                                    : globalIdx + 1
                                  : idx + 1;
                              const cellBase = {
                                border: "1px solid #000",
                                padding: "5px 2px",
                                fontSize: "10px",
                                textAlign: "center",
                                verticalAlign: "middle",
                                overflow: "hidden",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                backgroundColor:
                                  idx % 2 === 0
                                    ? "rgba(255,255,255,0.7)"
                                    : "rgba(245,245,245,0.5)",
                              };
                              return (
                                <tr key={record.id || idx}>
                                  <td style={cellBase}>{serialNumber}</td>
                                  <td
                                    style={{
                                      ...cellBase,
                                      direction: isRTL ? "rtl" : "ltr",
                                      textAlign: isRTL ? "right" : "left",
                                    }}
                                  >
                                    {getEmployeeName(record.employee) || "—"}
                                  </td>
                                  <td style={cellBase}>
                                    {record.employee?.employee_code || "—"}
                                  </td>
                                  <td style={cellBase}>
                                    {formatDate(
                                      record.work_date ||
                                        record.date ||
                                        record.attendance_date,
                                    )}
                                  </td>
                                  <td style={cellBase}>
                                    {record.check_in
                                      ? formatTime(record.check_in)
                                      : record.check_in_time || "—"}
                                  </td>
                                  <td style={cellBase}>
                                    {record.check_out
                                      ? formatTime(record.check_out)
                                      : record.check_out_time || "—"}
                                  </td>
                                  <td style={cellBase}>
                                    {getStatusText(record.status)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <img
                        src={`${publicUrl}/FooterAce.png`}
                        alt="Footer"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                            borderTop: "4px double #000",
                            paddingTop: "8px",
                          display: "block",
                          marginTop: "12px",
                        }}
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </PdfPreviewModal>
    </div>
  );
};

// Print Template for All Attendance Records
const AllAttendancePrintTemplate = ({
  records,
  chunkIndex = 0,
  measureId,
  allRecordsList = [],
  isRTL,
  t,
  publicUrl,
  headerLogo,
  getEmployeeName,
  formatDate,
  formatTime,
  getStatusText,
}) => {
  if (!records || records.length === 0) return null;

  const elementId = measureId || `attendance-pdf-all-${chunkIndex}`;

  return (
    <div
      id={elementId}
      aria-hidden="true"
      style={{
        position: "absolute",
        left: "-9999px",
        top: 0,
        width: "794px",
        height: "auto",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            padding: "18px 18px 10px",
            position: "relative",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: isRTL ? "rtl" : "ltr",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "60%",
              backgroundPosition: "center top",
              backgroundRepeat: "no-repeat",
              opacity: 0.05,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                display: "block",
              }}
              crossOrigin="anonymous"
            />

            <div style={{ textAlign: "center", padding: "6px 0 8px", borderBottom: "1px solid #ddd", marginBottom: "4px", direction: isRTL ? "rtl" : "ltr" }}>
              <div style={{ fontSize: "16px", fontWeight: "900", color: "#000080" }}>{isRTL ? "تقرير الحضور" : "Attendance Report"}</div>
              <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>{isRTL ? "تاريخ التقرير" : "Report Date"}: {new Date().toLocaleDateString("en-US")}</div>
            </div>

            <div
              style={{
                marginTop: "12px",
                direction: isRTL ? "rtl" : "ltr",
                fontFamily: '"Times New Roman", "Times", serif',
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "11px",
                  border: "1px solid #000",
                  fontFamily: '"Times New Roman", "Times", serif',
                  backgroundColor: "rgba(255,255,255,0.7)",
                  tableLayout: "fixed",
                }}
              >
                <colgroup>
                  <col style={{ width: "3%" }} />
                  <col style={{ width: "28%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <thead>
                  <tr style={{ backgroundColor: "rgba(238,242,247,0.7)" }}>
                    {[
                      "#",
                      t("attendance.tableHeaders.employee", {
                        defaultValue: "الموظف",
                      }),
                      t("attendance.tableHeaders.employeeCode", {
                        defaultValue: "كود",
                      }),
                      t("attendance.tableHeaders.date", {
                        defaultValue: "التاريخ",
                      }),
                      t("attendance.tableHeaders.checkIn", {
                        defaultValue: "دخول",
                      }),
                      t("attendance.tableHeaders.checkOut", {
                        defaultValue: "خروج",
                      }),
                      t("attendance.tableHeaders.status", {
                        defaultValue: "الحالة",
                      }),
                    ].map(header => (
                      <th
                        key={header}
                        style={{
                          border: "1px solid #000",
                          padding: "6px 2px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          fontWeight: "700",
                          backgroundColor: "rgba(238,242,247,0.7)",
                          overflow: "hidden",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, idx) => {
                    const foundIdx = Array.isArray(allRecordsList)
                      ? allRecordsList.findIndex(
                          r => String(r?.id) === String(record?.id),
                        )
                      : -1;
                    const serialNumber =
                      foundIdx >= 0 ? foundIdx + 1 : chunkIndex * 25 + idx + 1;

                    const cellBase = {
                      border: "1px solid #000",
                      padding: "5px 2px",
                      fontSize: "10px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      overflow: "hidden",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      backgroundColor:
                        idx % 2 === 0
                          ? "rgba(255,255,255,0.7)"
                          : "rgba(245,245,245,0.5)",
                    };
                    return (
                      <tr key={record.id}>
                        <td style={cellBase}>{serialNumber}</td>
                        <td
                          style={{
                            ...cellBase,
                            direction: isRTL ? "rtl" : "ltr",
                            textAlign: isRTL ? "right" : "left",
                          }}
                        >
                          {getEmployeeName(record.employee) || "—"}
                        </td>
                        <td style={cellBase}>
                          {record.employee?.employee_code || "—"}
                        </td>
                        <td style={cellBase}>
                          {formatDate(
                            record.work_date ||
                              record.date ||
                              record.attendance_date,
                          )}
                        </td>
                        <td style={cellBase}>
                          {record.check_in
                            ? formatTime(record.check_in)
                            : record.check_in_time || "—"}
                        </td>
                        <td style={cellBase}>
                          {record.check_out
                            ? formatTime(record.check_out)
                            : record.check_out_time || "—"}
                        </td>
                        <td style={cellBase}>{getStatusText(record.status)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <img
              src={`${publicUrl}/FooterAce.png`}
              alt="Footer"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                display: "block",
                borderTop: "4px double #000",
                paddingTop: "8px",
              }}
              crossOrigin="anonymous"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const dropdownMenuStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  backgroundColor: "#1a1f2e",
  border: "1px solid #333",
  borderRadius: 8,
  zIndex: 2000,
  padding: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
};

const dropdownSearchStyle = {
  width: "100%",
  padding: 10,
  borderRadius: 6,
  border: "1px solid #333",
  backgroundColor: "#14263b",
  color: "white",
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: "center",
  color: "#ccc",
  fontSize: 13,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
  modalType = "checkIn",
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : i18n?.language === "ar";
  const normalizedDropdownSearch = String(searchTerm || "")
    .trim()
    .toLowerCase();
  const filteredItems = normalizedDropdownSearch
    ? (items || []).filter(item => {
        const searchable = [
          item.name,
          item.name_ar,
          item.name_en,
          item.employee_code,
          item.code,
        ]
          .filter(value => value !== undefined && value !== null)
          .join(" ")
          .toLowerCase();
        return searchable.includes(normalizedDropdownSearch);
      })
    : items || [];
  const shouldShowPagination =
    pagination &&
    filteredItems.length > 0 &&
    (!normalizedDropdownSearch ||
      filteredItems.length === (items || []).length) &&
    (pagination.last_page > 1 ||
      (pagination.last_page === 1 && pagination.current_page === 1));

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div
      style={{ ...dropdownMenuStyle, direction: dropdownIsRTL ? "rtl" : "ltr" }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={t(
            `attendance.modals.${modalType}.placeholders.searchEmployee`,
          )}
          value={searchTerm}
          onChange={e => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          autoFocus
          style={{
            ...dropdownSearchStyle,
            direction: dropdownIsRTL ? "rtl" : "ltr",
            textAlign: dropdownIsRTL ? "right" : "left",
          }}
        />
      </div>
      {shouldShowPagination && (
        <div
          style={{
            padding: "8px",
            borderTop: "1px solid #333",
            background: "#14263b",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 6,
            }}
          >
            <span
              style={{
                color: "#ccc",
                fontSize: 11,
                direction: dropdownIsRTL ? "rtl" : "ltr",
                textAlign: dropdownIsRTL ? "right" : "left",
              }}
            >
              {t(`attendance.modals.${modalType}.pagination.page`)}{" "}
              {pagination.current_page}{" "}
              {t(`attendance.modals.${modalType}.pagination.of`)}{" "}
              {pagination.last_page}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if (pagination.current_page > 1) onPageChange(1);
              }}
              disabled={pagination.current_page === 1}
              style={{
                padding: "4px 8px",
                backgroundColor:
                  pagination.current_page === 1 ? "#2a2a2a" : "#1a1f2e",
                color: pagination.current_page === 1 ? "#666" : "white",
                border: "1px solid #444",
                borderRadius: "4px",
                cursor:
                  pagination.current_page === 1 ? "not-allowed" : "pointer",
                fontSize: 10,
                fontWeight: "bold",
                minWidth: "28px",
              }}
            >
              ««
            </button>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if (pagination.current_page > 1)
                  onPageChange(pagination.current_page - 1);
              }}
              disabled={pagination.current_page === 1}
              style={{
                padding: "4px 8px",
                backgroundColor:
                  pagination.current_page === 1 ? "#2a2a2a" : "#1a1f2e",
                color: pagination.current_page === 1 ? "#666" : "white",
                border: "1px solid #444",
                borderRadius: "4px",
                cursor:
                  pagination.current_page === 1 ? "not-allowed" : "pointer",
                fontSize: 10,
                fontWeight: "bold",
                minWidth: "28px",
              }}
            >
              ‹
            </button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1)
                start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map(page => (
                <button
                  key={page}
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onPageChange(page);
                  }}
                  style={{
                    padding: "4px 8px",
                    backgroundColor: page === current ? "#AC2000" : "#1a1f2e",
                    color: "white",
                    border:
                      page === current ? "1px solid #AC2000" : "1px solid #444",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: 10,
                    fontWeight: page === current ? "bold" : "normal",
                    minWidth: "28px",
                  }}
                >
                  {page}
                </button>
              ));
            })()}
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if (pagination.current_page < pagination.last_page)
                  onPageChange(pagination.current_page + 1);
              }}
              disabled={pagination.current_page === pagination.last_page}
              style={{
                padding: "4px 8px",
                backgroundColor:
                  pagination.current_page === pagination.last_page
                    ? "#2a2a2a"
                    : "#1a1f2e",
                color:
                  pagination.current_page === pagination.last_page
                    ? "#666"
                    : "white",
                border: "1px solid #444",
                borderRadius: "4px",
                cursor:
                  pagination.current_page === pagination.last_page
                    ? "not-allowed"
                    : "pointer",
                fontSize: 10,
                fontWeight: "bold",
                minWidth: "28px",
              }}
            >
              ›
            </button>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                if (pagination.current_page < pagination.last_page)
                  onPageChange(pagination.last_page);
              }}
              disabled={pagination.current_page === pagination.last_page}
              style={{
                padding: "4px 8px",
                backgroundColor:
                  pagination.current_page === pagination.last_page
                    ? "#2a2a2a"
                    : "#1a1f2e",
                color:
                  pagination.current_page === pagination.last_page
                    ? "#666"
                    : "white",
                border: "1px solid #444",
                borderRadius: "4px",
                cursor:
                  pagination.current_page === pagination.last_page
                    ? "not-allowed"
                    : "pointer",
                fontSize: 10,
                fontWeight: "bold",
                minWidth: "28px",
              }}
            >
              »»
            </button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 260, overflowY: "auto" }}>
        {filteredItems.length === 0 ? (
          <div
            style={{
              ...dropdownEmptyStyle,
              direction: dropdownIsRTL ? "rtl" : "ltr",
              textAlign: dropdownIsRTL ? "right" : "left",
            }}
          >
            {t(`attendance.modals.${modalType}.pagination.noResults`)}
          </div>
        ) : (
          filteredItems.map(item => (
            <div
              key={item.id}
              style={{
                padding: "10px 12px",
                borderTop: "1px solid #333",
                cursor: "pointer",
                direction: dropdownIsRTL ? "rtl" : "ltr",
                textAlign: dropdownIsRTL ? "right" : "left",
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = "#2a2a2a";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {(dropdownIsRTL ? item.name_ar : item.name_en) || item.name} -{" "}
              {item.employee_code}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
export default Attendance;
